import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { clearDefects, setProcParams } from "../../redux/defectsSlice";
import { initApproval } from "../../redux/defectsListSlice";
import "./DefectsListGrid.css";

import {
  GridApi,
  ColDef,
  GridOptions,
  RowClassParams,
} from "ag-grid-enterprise";
import { CellClickedEvent } from "ag-grid-community";
import {
  ICellRendererParams,
  TextMatcherParams,
} from "@ag-grid-community/core";

import BtnEditRenderer from "./BtnEditRenderer";
import BtnApprovalRenderer from "./BtnApprovalRenderer";

import Modal from "../ui/Modal";
import DefectsApproval from "../defectslist/DefectsApproval";

// CSS
import "./DefectsListGrid.css";
import "ag-grid-enterprise/styles/ag-grid.css";
import "ag-grid-enterprise/styles/ag-theme-balham.css";

interface GridProps {
  onGridReadyParent: (params: { api: any }) => void;
  reloadDefectsList: () => void;
  gridApi: GridApi<any> | null;
}

const Grid: React.FC<GridProps> = ({
  onGridReadyParent,
  reloadDefectsList,
  gridApi,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const [isDefectsApprovalModalOpen, setIsDefectsApprovalModalOpen] =
    useState(false);
  const [rowIndex, setRowIndex] = useState(0);

  // 工場・加工・機械
  const plant_code = useSelector(
    (state: RootState) => state.queryParams.selectedPlantCode
  );
  const proc_code = useSelector(
    (state: RootState) => state.queryParams.selectedProcCode
  );
  const machCode = useSelector(
    (state: RootState) => state.queryParams.machCode
  );

  // グリッドの初期化
  const onGridReady = (params: { api: GridApi }) => {
    onGridReadyParent(params);
  };

  const defectsList = useSelector((state: RootState) => state.defectsList.rows);
  const openDefectApproval = (e: CellClickedEvent) => {
    if (e.data.id != null) {
      setRowIndex(e.data.id);
      dispatch(initApproval(defectsList[e.data.id]));
    }
    setIsDefectsApprovalModalOpen(true);
  };

  // 不良確認モーダルのクローズ
  const closeDefectsApprovalModal = () => {
    setIsDefectsApprovalModalOpen(false);
    reloadDefectsList();
  };

  const navigate = useNavigate();
  const linkDefects = (e: CellClickedEvent) => {
    console.log(e.data);
    console.log("不良情報初期化");
    dispatch(clearDefects());

    // 選択した不良情報の加工、機械コードを不良登録のstateにセット
    dispatch(
      setProcParams({
        plantCode: plant_code,
        procCode: e.data.registering_proc_code,
        machCode: e.data.registering_mach_code,
        poDescNum: e.data.po_desc_num,
      })
    );
    navigate("/defects");
  };

  // 行のCSSクラスの設定
  const rowClassRules = {
    "defects-row-add": (params: RowClassParams) => {
      return params.data.is_add === true;
    },
  };

  const columns: ColDef[] = [
    {
      headerName: "",
      field: "",
      width: 38,
      cellRenderer: BtnEditRenderer,
      onCellClicked: linkDefects,
      cellStyle: { padding: "0 4px" },
    },
    {
      headerName: "",
      field: "",
      width: 38,
      cellRenderer: BtnApprovalRenderer,
      onCellClicked: openDefectApproval,
      cellStyle: { padding: "0 4px" },
    },
    {
      headerName: "注文No",
      field: "po_data_num",
      width: 100,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "受注No",
      field: "po_desc_num",
      width: 120,
      cellClass: "po-desc-num",
      filter: "agTextColumnFilter",
    },
    {
      headerName: "工順",
      field: "mach_seq",
      width: 80,
      filter: "agNumberColumnFilter",
    },
    {
      headerName: "発生回数",
      field: "failure_count",
      width: 100,
      filter: "agNumberColumnFilter",
    },
    {
      headerName: "不良枚数",
      field: "scrap_qty",
      width: 100,
      filter: "agNumberColumnFilter",
    },
    {
      headerName: "得意先名",
      field: "customer_name",
      width: 200,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "送り先名",
      field: "destin_name",
      width: 200,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "１次承認者",
      width: 100,
      cellDataType: "string",
      filter: "agTextColumnFilter",
      field: "review_history",
      valueFormatter: (params: any) => {
        const reviewHistory = params.value;
        if (reviewHistory === null) {
          return "";
        }
        const level1 = reviewHistory
          .filter((d: { reviewer_level: number }) => d.reviewer_level === 1)
          .sort((a: { review_seq: number }, b: { review_seq: number }) =>
            a.review_seq > b.review_seq ? -1 : 1
          );
        return Array.from(
          new Set(level1.map((d: { reviewer_name: any }) => d.reviewer_name))
        ).join(", ");
      },
      filterValueGetter: (params: any) => {
        // AG-Gridで使用するフィルタ用に最新の１次承認者の配列を返す
        const reviewHistory = params.data.review_history;
        if (reviewHistory === null) {
          return "";
        }
        const level1 = reviewHistory
          .filter((d: { reviewer_level: number }) => d.reviewer_level === 1)
          .sort((a: { review_seq: number }, b: { review_seq: number }) =>
            a.review_seq > b.review_seq ? -1 : 1
          );
        return Array.from(
          new Set(level1.map((d: { reviewer_name: any }) => d.reviewer_name))
        ).join(",");
      },
      filterParams: {
        textMatcher: (props: TextMatcherParams) => {
          if (props.filterText === null) {
            return false;
          }
          if (props.value.length === null || props.value.length === 0) {
            return false;
          }
          if (typeof props.value !== "string") {
            return false;
          }
          const filterText = props.filterText;
          const vales = props.value.toString().split(",");
          let match = false;
          vales.forEach((value: string) => {
            switch (props.filterOption) {
              case "contains":
                if (value.indexOf(filterText) >= 0) {
                  match = true;
                }
                break;
              case "notContains":
                if (value.indexOf(filterText) < 0) {
                  match = true;
                }
                break;
              case "equals":
                if (value === filterText) {
                  match = true;
                }
                break;
              case "notEqual":
                if (value !== filterText) {
                  match = true;
                }
                break;
              case "startsWith":
                if (value.indexOf(filterText) === 0) {
                  match = true;
                }
                break;
              case "endsWith":
                const index = value.lastIndexOf(filterText);
                if (index >= 0 && index === value.length - filterText.length) {
                  match = true;
                }
                break;
              default:
                // should never happen
                console.warn("invalid filter type " + props.filterOption);
                break;
            }
          });
          return match;
        },
      },
    },
    {
      headerName: "１次承認",
      field: "approval",
      width: 80,
      cellDataType: "string",
      filter: "agSetColumnFilter",
      filterValueGetter: (params) => {
        // AG-Gridで使用するフィルタ用に最新の1次承認ステータスを返す
        const reviewHistory = params.data.review_history;
        if (reviewHistory === null) {
          return "";
        }
        const level1 = reviewHistory
          .filter((d: { reviewer_level: number }) => d.reviewer_level === 1)
          .sort((a: { review_seq: number }, b: { review_seq: number }) =>
            a.review_seq > b.review_seq ? -1 : 1
          );
        return level1[0].review_status;
      },
      cellRenderer: (props: ICellRendererParams) => {
        const reviewHistory = props.data.review_history;
        if (reviewHistory === null) {
          return (
            <img
              className="defects-review-status"
              src="images/pending.svg"
              alt="保留"
            />
          );
        }
        const level1 = reviewHistory
          .filter((d: { reviewer_level: number }) => d.reviewer_level === 1)
          .sort((a: { review_seq: number }, b: { review_seq: number }) =>
            a.review_seq > b.review_seq ? -1 : 1
          );
        if (level1[0].review_status === "rejected") {
          return (
            <img
              className="defects-review-status"
              src="images/unapproved.svg"
              alt="却下"
            />
          );
        }
        if (level1[0].review_status === "approved") {
          return (
            <img
              className="defects-review-status"
              src="images/approved.svg"
              alt="承認"
            />
          );
        }
        return (
          <img
            className="defects-review-status"
            src="images/pending.svg"
            alt="保留"
          />
        );
      },
    },
    {
      headerName: "１次承認コメント",
      field: "review_history",
      valueFormatter: (params: any) => {
        const reviewHistory = params.value;
        if (reviewHistory === null) {
          return "";
        }
        const level1 = reviewHistory
          .filter((d: { reviewer_level: number }) => d.reviewer_level === 1)
          .sort((a: { review_seq: number }, b: { review_seq: number }) =>
            a.review_seq > b.review_seq ? -1 : 1
          );
        if (level1.length > 0) {
          return level1[0].comments;
        } else {
          return "";
        }
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (params: any) => {
        // AG-Gridで使用するフィルタ用に最新の1次承認者のコメント配列を返す
        const reviewHistory = params.data.review_history;
        if (reviewHistory === null) {
          return "";
        }
        const level1 = reviewHistory
          .filter((d: { reviewer_level: number }) => d.reviewer_level === 1)
          .sort((a: { review_seq: number }, b: { review_seq: number }) =>
            a.review_seq > b.review_seq ? -1 : 1
          );
        return Array.from(
          new Set(level1.map((d: { comments: any }) => d.comments))
        ).join(",");
      },
      filterParams: {
        textMatcher: (props: TextMatcherParams) => {
          if (props.filterText === null) {
            return false;
          }
          if (props.value.length === null || props.value.length === 0) {
            return false;
          }
          if (typeof props.value !== "string") {
            return false;
          }
          const filterText = props.filterText;
          const vales = props.value.toString().split(",");
          let match = false;
          vales.forEach((value: string) => {
            switch (props.filterOption) {
              case "contains":
                if (value.indexOf(filterText) >= 0) {
                  match = true;
                }
                break;
              case "notContains":
                if (value.indexOf(filterText) < 0) {
                  match = true;
                }
                break;
              case "equals":
                if (value === filterText) {
                  match = true;
                }
                break;
              case "notEqual":
                if (value !== filterText) {
                  match = true;
                }
                break;
              case "startsWith":
                if (value.indexOf(filterText) === 0) {
                  match = true;
                }
                break;
              case "endsWith":
                const index = value.lastIndexOf(filterText);
                if (index >= 0 && index === value.length - filterText.length) {
                  match = true;
                }
                break;
              default:
                // should never happen
                console.warn("invalid filter type " + props.filterOption);
                break;
            }
          });
          return match;
        },
      },
    },
    {
      headerName: "２次承認者",
      width: 100,
      filter: "agTextColumnFilter",
      field: "review_history",
      valueFormatter: (params: any) => {
        const reviewHistory = params.value;
        if (reviewHistory === null) {
          return "";
        }
        const level2 = reviewHistory
          .filter((d: { reviewer_level: number }) => d.reviewer_level === 2)
          .sort((a: { review_seq: number }, b: { review_seq: number }) =>
            a.review_seq > b.review_seq ? -1 : 1
          );
        return Array.from(
          new Set(level2.map((d: { reviewer_name: any }) => d.reviewer_name))
        ).join(", ");
      },
      filterValueGetter: (params: any) => {
        // AG-Gridで使用するフィルタ用に最新の2次承認者の配列を返す
        const reviewHistory = params.data.review_history;
        if (reviewHistory === null) {
          return "";
        }
        const level2 = reviewHistory
          .filter((d: { reviewer_level: number }) => d.reviewer_level === 2)
          .sort((a: { review_seq: number }, b: { review_seq: number }) =>
            a.review_seq > b.review_seq ? -1 : 1
          );
        return Array.from(
          new Set(level2.map((d: { reviewer_name: any }) => d.reviewer_name))
        ).join(",");
      },
      filterParams: {
        textMatcher: (props: TextMatcherParams) => {
          if (props.filterText === null) {
            return false;
          }
          if (props.value.length === null || props.value.length === 0) {
            return false;
          }
          if (typeof props.value !== "string") {
            return false;
          }
          const filterText = props.filterText;
          const vales = props.value.toString().split(",");
          let match = false;
          vales.forEach((value: string) => {
            switch (props.filterOption) {
              case "contains":
                if (value.indexOf(filterText) >= 0) {
                  match = true;
                }
                break;
              case "notContains":
                if (value.indexOf(filterText) < 0) {
                  match = true;
                }
                break;
              case "equals":
                if (value === filterText) {
                  match = true;
                }
                break;
              case "notEqual":
                if (value !== filterText) {
                  match = true;
                }
                break;
              case "startsWith":
                if (value.indexOf(filterText) === 0) {
                  match = true;
                }
                break;
              case "endsWith":
                const index = value.lastIndexOf(filterText);
                if (index >= 0 && index === value.length - filterText.length) {
                  match = true;
                }
                break;
              default:
                // should never happen
                console.warn("invalid filter type " + props.filterOption);
                break;
            }
          });
          return match;
        },
      },
    },
    {
      headerName: "２次承認",
      field: "factoryManagerApproval",
      width: 80,
      cellDataType: "string",
      filter: "agSetColumnFilter",
      filterValueGetter: (params) => {
        // AG-Gridで使用するフィルタ用に最新の2次承認ステータスを返す
        const reviewHistory = params.data.review_history;
        if (reviewHistory === null) {
          return "";
        }
        const level2 = reviewHistory
          .filter((d: { reviewer_level: number }) => d.reviewer_level === 2)
          .sort((a: { review_seq: number }, b: { review_seq: number }) =>
            a.review_seq > b.review_seq ? -1 : 1
          );
        return level2[0].review_status;
      },
      cellRenderer: (props: ICellRendererParams) => {
        const reviewHistory = props.data.review_history;
        if (reviewHistory === null) {
          return (
            <img
              className="defects-review-status"
              src="images/pending.svg"
              alt="保留"
            />
          );
        }
        const level2 = reviewHistory
          .filter((d: { reviewer_level: number }) => d.reviewer_level === 2)
          .sort((a: { review_seq: number }, b: { review_seq: number }) =>
            a.review_seq > b.review_seq ? -1 : 1
          );
        if (level2[0].review_status === "rejected") {
          return (
            <img
              className="defects-review-status"
              src="images/unapproved.svg"
              alt="却下"
            />
          );
        }
        if (level2[0].review_status === "approved") {
          return (
            <img
              className="defects-review-status"
              src="images/approved.svg"
              alt="承認"
            />
          );
        }
        return (
          <img
            className="defects-review-status"
            src="images/pending.svg"
            alt="保留"
          />
        );
      },
    },
    {
      headerName: "２次承認コメント",
      field: "review_history",
      valueFormatter: (params: any) => {
        const reviewHistory = params.value;
        if (reviewHistory === null) {
          return "";
        }
        const level2 = reviewHistory
          .filter((d: { reviewer_level: number }) => d.reviewer_level === 2)
          .sort((a: { review_seq: number }, b: { review_seq: number }) =>
            a.review_seq > b.review_seq ? -1 : 1
          );
        if (level2.length > 0) {
          return level2[0].comments;
        } else {
          return "";
        }
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (params: any) => {
        // AG-Gridで使用するフィルタ用に最新の2次承認者のコメント配列を返す
        const reviewHistory = params.data.review_history;
        if (reviewHistory === null) {
          return "";
        }
        const level2 = reviewHistory
          .filter((d: { reviewer_level: number }) => d.reviewer_level === 2)
          .sort((a: { review_seq: number }, b: { review_seq: number }) =>
            a.review_seq > b.review_seq ? -1 : 1
          );
        return Array.from(
          new Set(level2.map((d: { comments: any }) => d.comments))
        ).join(",");
      },
      filterParams: {
        textMatcher: (props: TextMatcherParams) => {
          if (props.filterText === null) {
            return false;
          }
          if (props.value.length === null || props.value.length === 0) {
            return false;
          }
          if (typeof props.value !== "string") {
            return false;
          }
          const filterText = props.filterText;
          const vales = props.value.toString().split(",");
          let match = false;
          vales.forEach((value: string) => {
            switch (props.filterOption) {
              case "contains":
                if (value.indexOf(filterText) >= 0) {
                  match = true;
                }
                break;
              case "notContains":
                if (value.indexOf(filterText) < 0) {
                  match = true;
                }
                break;
              case "equals":
                if (value === filterText) {
                  match = true;
                }
                break;
              case "notEqual":
                if (value !== filterText) {
                  match = true;
                }
                break;
              case "startsWith":
                if (value.indexOf(filterText) === 0) {
                  match = true;
                }
                break;
              case "endsWith":
                const index = value.lastIndexOf(filterText);
                if (index >= 0 && index === value.length - filterText.length) {
                  match = true;
                }
                break;
              default:
                // should never happen
                console.warn("invalid filter type " + props.filterOption);
                break;
            }
          });
          return match;
        },
      },
    },
  ];

  const defaultColDef = {
    resizable: true,
    floatingFilter: true,
    sortable: false,
  };

  // コンポーネントのロジック
  const gridOptions: GridOptions = {
    defaultColDef: defaultColDef,
    columnDefs: columns,
    rowClassRules: rowClassRules,
    rowHeight: 35,
    //
    getRowId: (params) => {
      return params.data.id;
    },
  };

  return (
    // 描画するReact要素
    <div className="ag-theme-balham">
      <AgGridReact
        {...gridOptions}
        //rowData={rowData}
        domLayout={"normal"} // テーブルの高さを自動調整
        onGridReady={onGridReady}
      />

      <Modal
        isOpen={isDefectsApprovalModalOpen}
        onClose={closeDefectsApprovalModal}
        width="90vw"
        height="88vh"
        contentClassName={""}
      >
        <DefectsApproval index={rowIndex} gridApi={gridApi} />
      </Modal>
    </div>
  );
};

export default Grid;
